import { Autocomplete } from '@react-google-maps/api';
import { message } from 'antd';
import { Header, SideMenuTitle } from 'features/SideMenu';
import { setIsSideMenuCollapsed } from 'features/vicinityMapSlice';
import gaFunction from 'lib/ga';
import { useVicinityMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';
import { getGMCoordinates, handleMapIt } from 'lib/utils';
import { useState } from 'react';
import styled from 'styled-components';

interface Props {
  directionsRenderer?: google.maps.DirectionsRenderer;
  map?: google.maps.Map;
}

const MapIt = ({ map, directionsRenderer }: Props) => {
  const dispatch = useAppDispatch();
  // initialize state
  const { currPOIs } = useAppSelector((state) => ({
    currPOIs: state.vicinityMap.currPOIs!,
  }));

  const { isMobile } = useWindowDimensions();

  const vicinityMapData = useVicinityMapQuery().data!;

  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete>();

  // when autocomplete loads, set state to the autocomplete object
  function onAutocompleteLoad(autocomplete: google.maps.places.Autocomplete) {
    setAutocomplete(autocomplete);
  }

  function onPlaceChanged() {
    if (map && directionsRenderer) {
      // GA
      gaFunction.vmUseMapItFeature();
      // collapses sideMenu once user enters place change
      if (isMobile) {
        dispatch(setIsSideMenuCollapsed(true));
      }
      if (
        !autocomplete ||
        !autocomplete.getPlace() ||
        !autocomplete.getPlace().geometry?.location
      ) {
        message.error('Map it failed. Please select a suggested autocomplete query.');
      } else {
        handleMapIt({
          map,
          directionsRenderer,
          currPOIs,
          origin: getGMCoordinates(vicinityMapData.main.projectLocationCoordinates),
          destination: autocomplete.getPlace().geometry!.location!,
          title: autocomplete.getPlace().name!,
          address: autocomplete.getPlace().formatted_address!,
          dispatch,
        });
      }
    }
  }

  return (
    <StyledMapItContainer id="map-it-container" className="cy-map-it" data-cy="map-it">
      <Header>
        <SideMenuTitle id="map-it-title">MAP TO A LOCATION</SideMenuTitle>
      </Header>
      {map && directionsRenderer ? (
        <Autocomplete
          onLoad={onAutocompleteLoad}
          onPlaceChanged={onPlaceChanged}
          bounds={
            new google.maps.LatLngBounds(
              getGMCoordinates(vicinityMapData.main.projectLocationCoordinates)
            )
          }
          options={{
            strictBounds: false,
          }}
          fields={['formatted_address', 'geometry.location', 'name', 'url']}
        >
          <MapItInput
            id="map-it-input"
            data-cy="map-it-input"
            placeholder={'📍 Enter a name, city, state, or zip code.'}
          />
        </Autocomplete>
      ) : (
        <MapItInput
          id="map-it-input"
          data-cy="map-it-input"
          placeholder={'📍 Enter a name, city, state, or zip code.'}
        />
      )}
    </StyledMapItContainer>
  );
};

export default MapIt;

export const StyledMapItContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 2vh 0 15vh 0;
`;

export const MapItInput = styled.input`
  width: 100%;
  background: white;
  border-radius: 8px;
  border: 1px solid #c3c2c1;
  color: black;
  font-size: clamp(12px, 1.1vw, 14px);
  padding: 2% 4%;
  text-align: center;

  &:focus {
    outline: ${({ theme }) => `1px solid ${theme.colors.primaryColor}`};
  }
`;
